// src/components/Schedule.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "../utils/api";
import {
  FaTrash,
  FaClock,
  FaCalendarAlt,
  FaBell,
  FaPlus,
  FaSpinner,
  FaVolumeUp,
  FaPlay,
  FaRedo,
  FaGavel,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

import { getFcmToken, listenToForegroundMessages } from "../push/firebase";

function Schedule() {
  const [meetings, setMeetings] = useState([]);
  const [cases, setCases] = useState([]);

  const [form, setForm] = useState({
    title: "",
    date: "",
    time: "",
    desc: "",
    remindMinutes: 0,
    relatedCase: "", // ✅ NEW
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [pushStatus, setPushStatus] = useState({
    tried: false,
    enabled: false,
    message: "",
  });

  /* =========================================================================
     🔊 Sound System (Stable)  ✅ زي ما كان (مهم جدًا)
  ========================================================================= */

  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const audioSupportedRef = useRef(true);
  const lastSoundAtRef = useRef(0);

  const SOUND_KEY = "ALAVVOCATO_PUSH_SOUND_UNLOCKED";
  const unsubscribeRef = useRef(null);

  const initAudio = () => {
    try {
      if (!audioRef.current) {
        const audio = new Audio("/notify.mp3");
        audio.preload = "auto";
        audio.volume = 1;
        audioRef.current = audio;
      }
    } catch (e) {
      audioSupportedRef.current = false;
      console.log("❌ Audio init failed:", e?.message);
    }
  };

  const unlockAudio = useCallback(async (silent = false) => {
    try {
      if (!audioSupportedRef.current) {
        if (!silent) toast.error("❌ المتصفح لا يدعم الصوت");
        return false;
      }

      if (audioUnlockedRef.current) {
        if (!silent) toast.success("✅ الصوت شغال بالفعل");
        return true;
      }

      initAudio();
      if (!audioRef.current) return false;

      // ✅ unlock trick
      audioRef.current.muted = true;
      await audioRef.current.play();
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.muted = false;

      audioUnlockedRef.current = true;
      localStorage.setItem(SOUND_KEY, "1");

      if (!silent) toast.success("✅ تم تشغيل صوت الإشعارات");
      return true;
    } catch (e) {
      if (!silent) {
        toast.error("⚠️ المتصفح منع تشغيل الصوت.. اضغط تفعيل الصوت مرة أخرى");
      }
      return false;
    }
  }, []);

  const playSound = useCallback(async () => {
    try {
      if (!audioSupportedRef.current) return;

      // anti spam
      if (Date.now() - lastSoundAtRef.current < 1200) return;
      lastSoundAtRef.current = Date.now();

      initAudio();
      if (!audioRef.current) return;

      // ✅ Never play before unlock
      if (!audioUnlockedRef.current) {
        toast("🔕 لتفعيل صوت الإشعارات اضغط (تفعيل الصوت)", { icon: "⚠️" });
        return;
      }

      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      await audioRef.current.play();
    } catch (e) {
      console.log("⚠️ Sound blocked:", e?.message);
    }
  }, []);

  const testSound = useCallback(async () => {
    const ok = await unlockAudio(false);
    if (!ok) return;

    toast("🔊 تشغيل صوت تجريبي...", { icon: "✅" });
    await playSound();
  }, [unlockAudio, playSound]);

  /* =========================================================================
     Helpers
  ========================================================================= */

  const formatDateAr = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("ar-EG", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const normalizeText = (txt = "") =>
    String(txt || "").replace(/\\n/g, "\n").trim();

  /* =========================================================================
     Meetings sorting (B) ✅ ترتيب حسب التاريخ فقط (المحترم)
  ========================================================================= */

  const sortedMeetings = useMemo(() => {
    const toDT = (m) => {
      const d = new Date(m.date);
      if (m.time) {
        const [hh, mm] = m.time.split(":").map(Number);
        d.setHours(hh || 0, mm || 0, 0, 0);
      } else {
        d.setHours(9, 0, 0, 0);
      }
      return d.getTime();
    };

    // ✅ sort by date/time only
    return [...meetings].sort((a, b) => toDT(a) - toDT(b));
  }, [meetings]);

  /* =========================================================================
     Fetch meetings / cases
  ========================================================================= */

  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/meetings");
      setMeetings(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("❌ فشل تحميل المواعيد");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCases = useCallback(async () => {
    try {
      // ✅ هنجيب عدد كبير علشان dropdown
      const res = await api.get(`/cases?page=1&limit=1000&q=`);
      setCases(Array.isArray(res.data?.cases) ? res.data.cases : []);
    } catch {
      setCases([]);
    }
  }, []);

  /* =========================================================================
     Push Setup ✅ زي ما كان (مهم جدًا)
  ========================================================================= */

  const setupPush = useCallback(async () => {
    try {
      setPushStatus({
        tried: true,
        enabled: false,
        message: "⏳ جاري تفعيل الإشعارات...",
      });

      const token = await getFcmToken();

      if (!token) {
        setPushStatus({
          tried: true,
          enabled: false,
          message:
            "⚠️ الإشعارات غير مفعلة (تم رفض الإذن أو المتصفح لا يدعم).",
        });
        return;
      }

      await api.post("/push/register", { token, platform: "web" });

      setPushStatus({
        tried: true,
        enabled: true,
        message: "✅ إشعارات المواعيد مفعّلة بنجاح.",
      });
    } catch (e) {
      console.log("❌ Push setup failed:", e);
      setPushStatus({
        tried: true,
        enabled: false,
        message: "⚠️ فشل تفعيل الإشعارات.. راجع إعدادات Firebase.",
      });
    }
  }, []);

  /* =========================================================================
     Foreground UI Notification ✅ زي ما كان
  ========================================================================= */

  const showForegroundToast = ({ title, body, image }) => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-md w-full bg-gray-950 border border-yellow-500/50 shadow-2xl rounded-2xl pointer-events-auto flex p-3 gap-3`}
          dir="rtl"
        >
          {image ? (
            <div className="w-14 h-14 rounded-xl overflow-hidden border border-gray-800 flex-shrink-0">
              <img
                src={image}
                alt="Notification"
                className="w-full h-full object-cover"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </div>
          ) : (
            <div className="w-14 h-14 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center text-yellow-400 flex-shrink-0">
              <FaBell />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-yellow-300 break-words">
              {title}
            </p>

            {body ? (
              <p className="text-xs text-gray-300 mt-1 whitespace-pre-line break-words leading-relaxed">
                {body}
              </p>
            ) : null}

            <div className="mt-2 flex justify-between items-center gap-2">
              <button
                onClick={() => toast.dismiss(t.id)}
                className="text-xs px-3 py-1 rounded-lg bg-gray-800 hover:bg-gray-700 text-white font-bold transition"
              >
                إغلاق
              </button>

              {!audioUnlockedRef.current ? (
                <button
                  onClick={() => unlockAudio(false)}
                  className="text-xs px-3 py-1 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold transition flex items-center gap-2"
                >
                  <FaVolumeUp />
                  تفعيل الصوت
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ),
      { duration: 8500 }
    );
  };

  /* =========================================================================
     Init
  ========================================================================= */

  useEffect(() => {
    fetchMeetings();
    fetchCases(); // ✅ NEW
    setupPush();

    // restore sound state
    if (localStorage.getItem(SOUND_KEY) === "1") {
      audioUnlockedRef.current = true;
    }

    // ✅ auto unlock silently on first gestures (stable)
    const autoUnlock = () => unlockAudio(true);
    window.addEventListener("click", autoUnlock);
    window.addEventListener("touchstart", autoUnlock);

    // ✅ Foreground listener - only once
    (async () => {
      if (typeof unsubscribeRef.current === "function") {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      unsubscribeRef.current = await listenToForegroundMessages((payload) => {
        const title =
          payload?.notification?.title || payload?.data?.title || "🔔 تنبيه";
        const body = payload?.notification?.body || payload?.data?.body || "";
        const image =
          payload?.notification?.image || payload?.data?.image || "";

        showForegroundToast({
          title: normalizeText(title),
          body: normalizeText(body),
          image: image || null,
        });

        // ✅ sound in foreground
        playSound();
      });
    })();

    return () => {
      window.removeEventListener("click", autoUnlock);
      window.removeEventListener("touchstart", autoUnlock);

      if (typeof unsubscribeRef.current === "function") {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================================================================
     Actions
  ========================================================================= */

  const addMeeting = async () => {
    if (!form.title?.trim() || !form.date) {
      setNotice("");
      setError("⚠️ لازم تدخل عنوان وتاريخ أولاً");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setNotice("");

      const remindMinutes = Number(form.remindMinutes) || 0;

      const res = await api.post("/meetings", {
        title: form.title.trim(),
        date: form.date,
        time: form.time,
        desc: form.desc?.trim(),
        remindMinutes,
        relatedCase: form.relatedCase || null, // ✅ NEW
      });

      const created = res?.data?.meeting || res?.data;

      if (created) setMeetings((prev) => [created, ...prev]);
      else await fetchMeetings();

      setForm({
        title: "",
        date: "",
        time: "",
        desc: "",
        remindMinutes: 0,
        relatedCase: "",
      });

      setNotice("✅ تم إضافة الميعاد بنجاح");
    } catch {
      setError("❌ فشل إضافة الميعاد");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm("هل تريد حذف هذا الميعاد؟")) return;

    try {
      setError("");
      setNotice("");
      setMeetings((prev) => prev.filter((m) => m._id !== id));
      await api.delete(`/meetings/${id}`);
      setNotice("✅ تم حذف الميعاد");
    } catch {
      setError("❌ فشل حذف الميعاد");
      fetchMeetings();
    }
  };

  const openCase = (caseId) => {
    if (!caseId) return;
    window.location.href = `/cases/${caseId}`;
  };

  /* =========================================================================
     UI
  ========================================================================= */

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-black to-gray-900 text-white p-4 sm:p-6">
      <div className="max-w-5xl mx-auto mb-6">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-yellow-400 text-center">
          📅 المواعيد القادمة
        </h2>
        <p className="text-center text-gray-400 text-sm sm:text-base mt-2">
          نظم مواعيدك
        </p>
      </div>

      {/* Push Status ✅ زي ما كان */}
      <div className="max-w-5xl mx-auto mb-6" dir="rtl">
        <div
          className={`p-3 sm:p-4 rounded-xl border ${
            pushStatus.enabled
              ? "border-green-500 bg-green-500/10"
              : "border-yellow-500 bg-yellow-500/10"
          } flex items-start gap-3`}
        >
          <div className="mt-1">
            <FaBell
              className={`text-lg ${
                pushStatus.enabled ? "text-green-400" : "text-yellow-400"
              }`}
            />
          </div>

          <div className="flex-1">
            <p className="text-sm sm:text-base text-gray-200 leading-relaxed">
              {pushStatus.message ||
                "🔔 سيتم تفعيل إشعارات المواعيد تلقائيًا عند السماح بذلك."}
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {/* ✅ لا نشيل زر تفعيل الصوت */}
              <button
                onClick={() => unlockAudio(false)}
                className="text-xs px-3 py-2 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold transition flex items-center gap-2"
              >
                <FaVolumeUp />
                تفعيل صوت الإشعارات
              </button>

              {/* ✅ لا نشيل زر تجربة الصوت */}
              <button
                onClick={testSound}
                className="text-xs px-3 py-2 rounded-xl bg-gray-800 hover:bg-gray-700 text-white font-bold transition flex items-center gap-2"
              >
                <FaPlay />
                تجربة الصوت
              </button>

              {/* ✅ زر إعادة التفعيل */}
              <button
                onClick={setupPush}
                className="text-xs px-3 py-2 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-700 text-white font-bold transition flex items-center gap-2"
              >
                <FaRedo />
                إعادة تفعيل الإشعارات
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      <div className="max-w-5xl mx-auto space-y-3 mb-6" dir="rtl">
        {error && (
          <div className="p-3 rounded-xl border border-red-500 bg-red-500/10 text-red-200">
            {error}
          </div>
        )}
        {notice && (
          <div className="p-3 rounded-xl border border-green-500 bg-green-500/10 text-green-200">
            {notice}
          </div>
        )}
      </div>

      {/* Layout */}
      <div
        className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6"
        dir="rtl"
      >
        {/* Form */}
        <div className="bg-gray-900/70 backdrop-blur p-4 sm:p-6 rounded-2xl border border-yellow-500 shadow-xl">
          <h3 className="text-lg sm:text-xl font-bold text-yellow-300 mb-4 flex items-center gap-2">
            <FaPlus /> إضافة ميعاد
          </h3>

          <div className="space-y-4">
            <div>
              <label className="text-gray-300 text-sm">عنوان الميعاد</label>
              <input
                className="mt-1 p-3 rounded-xl bg-gray-800 text-white w-full focus:ring-2 focus:ring-yellow-400 outline-none placeholder-gray-400"
                placeholder="مثال: جلسة محكمة / مقابلة عميل"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>

            {/* ✅ ربط بالقضية */}
            <div>
              <label className="text-gray-300 text-sm">
                ربط الميعاد بقضية (اختياري)
              </label>
              <select
                className="mt-1 p-3 rounded-xl bg-gray-800 text-white w-full focus:ring-2 focus:ring-yellow-400 outline-none"
                value={form.relatedCase}
                onChange={(e) =>
                  setForm({ ...form, relatedCase: e.target.value })
                }
              >
                <option value="">بدون ربط بقضية</option>
                {cases.map((c) => (
                  <option key={c._id} value={c._id}>
                    ⚖️ {c.title}
                  </option>
                ))}
              </select>

              <p className="text-xs text-gray-500 mt-1">
                لو اخترت قضية → الميعاد هيتسجل داخل ملف القضية + يظهر هنا طبيعي
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-gray-300 text-sm">تاريخ الميعاد</label>
                <input
                  type="date"
                  className="mt-1 p-3 rounded-xl bg-gray-800 text-white w-full focus:ring-2 focus:ring-yellow-400 outline-none"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>

              <div>
                <label className="text-gray-300 text-sm">الوقت</label>
                <input
                  type="time"
                  className="mt-1 p-3 rounded-xl bg-gray-800 text-white w-full focus:ring-2 focus:ring-yellow-400 outline-none"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="text-gray-300 text-sm">تنبيه قبل الميعاد</label>
              <select
                className="mt-1 p-3 rounded-xl bg-gray-800 text-white w-full focus:ring-2 focus:ring-yellow-400 outline-none"
                value={form.remindMinutes}
                onChange={(e) =>
                  setForm({ ...form, remindMinutes: Number(e.target.value) })
                }
              >
                <option value={0}>في نفس وقت الميعاد بالظبط ✅</option>
                <option value={1}>قبلها بدقيقة</option>
                <option value={5}>قبلها بـ 5 دقائق</option>
                <option value={10}>قبلها بـ 10 دقائق</option>
                <option value={15}>قبلها بـ 15 دقيقة</option>
                <option value={30}>قبلها بـ 30 دقيقة</option>
              </select>
            </div>

            <div>
              <label className="text-gray-300 text-sm">الوصف / ملاحظات</label>
              <textarea
                className="mt-1 p-3 rounded-xl bg-gray-800 text-white w-full focus:ring-2 focus:ring-yellow-400 outline-none placeholder-gray-400"
                placeholder="تفاصيل إضافية تساعدك تتذكر طبيعة الميعاد"
                value={form.desc}
                rows={4}
                onChange={(e) => setForm({ ...form, desc: e.target.value })}
              />
            </div>

            <button
              onClick={addMeeting}
              disabled={submitting}
              className={`w-full rounded-xl font-bold py-3 transition flex items-center justify-center gap-2 ${
                submitting
                  ? "bg-yellow-400 opacity-70 text-black"
                  : "bg-yellow-500 hover:bg-yellow-600 text-black"
              }`}
            >
              {submitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  جاري الإضافة...
                </>
              ) : (
                <>
                  <FaPlus />
                  إضافة ميعاد
                </>
              )}
            </button>
          </div>
        </div>

        {/* List */}
        <div className="bg-gray-900/70 backdrop-blur p-4 sm:p-6 rounded-2xl border border-gray-700 shadow-xl">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
            <FaCalendarAlt className="text-yellow-400" /> قائمة المواعيد
          </h3>

          {loading ? (
            <div className="text-center text-yellow-400 py-10">
              ⏳ جاري التحميل...
            </div>
          ) : sortedMeetings.length > 0 ? (
            <div className="space-y-4">
              {sortedMeetings.map((m) => (
                <motion.div
                  key={m._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="rounded-2xl border border-gray-800 bg-gray-950/60 p-4 hover:border-yellow-500 transition"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-lg font-extrabold text-yellow-300 break-words">
                          {m.title}
                        </h4>

                        {/* ✅ Badge يوضح انه تابع لقضية */}
                        {m.relatedCase?.title ? (
                          <span className="text-xs px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 font-bold flex items-center gap-2">
                            <FaGavel />
                            مرتبط بقضية
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-2 space-y-1 text-sm text-gray-300">
                        <p className="flex items-center gap-2">
                          <FaCalendarAlt className="text-yellow-400" />
                          <span className="break-words">
                            {formatDateAr(m.date)}
                          </span>
                        </p>

                        <p className="flex items-center gap-2">
                          <FaClock className="text-yellow-400" />
                          <span>{m.time ? m.time : "بدون وقت محدد"}</span>
                        </p>

                        <p className="text-gray-400 mt-2 break-words">
                          {m.desc || "لا يوجد وصف"}
                        </p>

                        {/* ✅ اسم القضية + زر فتح */}
                        {m.relatedCase?.title ? (
                          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                            <p className="text-xs text-yellow-200 font-bold break-words">
                              ⚖️ القضية: {m.relatedCase.title}
                            </p>

                            {m.relatedCase?._id ? (
                              <button
                                onClick={() => openCase(m.relatedCase._id)}
                                className="text-xs px-3 py-2 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold transition flex items-center justify-center gap-2 w-full sm:w-auto"
                              >
                                <FaExternalLinkAlt />
                                فتح القضية
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 mt-2">
                            غير مرتبط بقضية
                          </p>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => deleteMeeting(m._id)}
                      className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition"
                    >
                      <FaTrash /> حذف
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400 py-10">
              لا يوجد مواعيد بعد
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto mt-10 text-center text-gray-500 text-xs">
        © {new Date().getFullYear()} — نظام مواعيد احترافي
      </div>
    </div>
  );
}

export default Schedule;
